import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceApi } from '../api/workspace';
import { EmptyState, Loading, PageError } from '../components/State';
import { isApiError } from '../lib/apiClient';
import { todayIso } from '../lib/format';
import './InvoicesPage.css';
import './BroadcastPage.css';

type Tab = 'message' | 'event';

export function BroadcastPage() {
  const [tab, setTab] = useState<Tab>('message');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<string | null>(null);
  const qc = useQueryClient();

  const clients = useQuery({ queryKey: ['clients'], queryFn: workspaceApi.clients });

  // message form
  const [text, setText] = useState('');
  // event form
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [isFree, setIsFree] = useState(true);
  const [cost, setCost] = useState('');
  const [currency, setCurrency] = useState('CAD');
  const [formUrl, setFormUrl] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [attachmentName, setAttachmentName] = useState('');

  const ids = [...selected];

  const sendMessage = useMutation({
    mutationFn: () => workspaceApi.broadcastMessage(ids, text.trim()),
    onSuccess: (r) => {
      setDone(`Message sent to ${r.sent} ${r.sent === 1 ? 'family' : 'families'}.`);
      setText('');
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });

  const sendEvent = useMutation({
    mutationFn: () =>
      workspaceApi.broadcastEvent({
        connectionIds: ids,
        title: title.trim(),
        date,
        time: time.trim() || undefined,
        location: location.trim() || undefined,
        notes: notes.trim() || undefined,
        isFree,
        cost: isFree ? 0 : parseFloat(cost) || 0,
        currency,
        formUrl: formUrl.trim() || undefined,
        attachmentUrl: attachmentUrl.trim() || undefined,
        attachmentName: attachmentName.trim() || undefined,
      }),
    onSuccess: (r) => {
      setDone(`Event created for ${r.count} ${r.count === 1 ? 'family' : 'families'}.`);
      setTitle('');
      setDate('');
      setTime('');
      setLocation('');
      setNotes('');
      setFormUrl('');
      setAttachmentUrl('');
      setAttachmentName('');
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['schedule'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const busy = sendMessage.isPending || sendEvent.isPending;
  const err = sendMessage.error ?? sendEvent.error;
  const canSend =
    ids.length > 0 &&
    (tab === 'message' ? text.trim().length > 0 : title.trim() && /^\d{4}-\d{2}-\d{2}$/.test(date));

  if (clients.isLoading) return <Loading />;

  const list = clients.data ?? [];

  return (
    <div>
      <Link to="/messages" className="yp__back">
        ‹ Messages
      </Link>
      <h1>Broadcast</h1>
      <p className="muted" style={{ marginTop: -2 }}>
        Send one message or event to several families at once.
      </p>

      {clients.isError ? (
        <PageError error={clients.error} onRetry={() => clients.refetch()} />
      ) : list.length === 0 ? (
        <EmptyState text="No connected families yet." />
      ) : (
        <>
          <section className="bc__section">
            <div className="bc__section-head">
              <h2 style={{ margin: 0 }}>Recipients</h2>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() =>
                  setSelected((prev) =>
                    prev.size === list.length
                      ? new Set()
                      : new Set(list.map((c) => c.connectionId)),
                  )
                }
              >
                {selected.size === list.length ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <div className="card bc__recipients">
              {list.map((c) => {
                const on = selected.has(c.connectionId);
                return (
                  <label key={c.connectionId} className="bc__recipient">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (on) next.delete(c.connectionId);
                          else next.add(c.connectionId);
                          return next;
                        })
                      }
                    />
                    {c.youthSnapshot?.name ?? 'Youth'}
                  </label>
                );
              })}
            </div>
          </section>

          <div className="tabbar" style={{ marginTop: 20 }}>
            <button
              className={`tabbtn${tab === 'message' ? ' is-active' : ''}`}
              onClick={() => setTab('message')}
            >
              Message
            </button>
            <button
              className={`tabbtn${tab === 'event' ? ' is-active' : ''}`}
              onClick={() => setTab('event')}
            >
              Event
            </button>
          </div>

          {tab === 'message' ? (
            <div className="field">
              <label>Message</label>
              <textarea
                className="bc__textarea"
                rows={4}
                value={text}
                maxLength={2000}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your message…"
              />
            </div>
          ) : (
            <>
              <div className="field">
                <label>Title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Session title" />
              </div>
              <div className="inv__two">
                <div className="field">
                  <label>Date</label>
                  <input type="date" min={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="field">
                  <label>Time</label>
                  <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="10:00 AM" />
                </div>
              </div>
              <div className="field">
                <label>Location</label>
                <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Address or online" />
              </div>
              <label className="bc__check">
                <input type="checkbox" checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />
                Free event
              </label>
              {!isFree && (
                <div className="inv__two">
                  <div className="field">
                    <label>Cost</label>
                    <input type="number" min={0} step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Currency</label>
                    <input value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
                  </div>
                </div>
              )}
              <div className="field">
                <label>Form link (optional)</label>
                <input value={formUrl} onChange={(e) => setFormUrl(e.target.value)} placeholder="https://…" />
              </div>
              <div className="inv__two">
                <div className="field">
                  <label>Attachment link (optional)</label>
                  <input value={attachmentUrl} onChange={(e) => setAttachmentUrl(e.target.value)} placeholder="https://…" />
                </div>
                <div className="field">
                  <label>Attachment name</label>
                  <input value={attachmentName} onChange={(e) => setAttachmentName(e.target.value)} placeholder="Document name" />
                </div>
              </div>
              <div className="field">
                <label>Notes</label>
                <textarea
                  className="bc__textarea"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes…"
                />
              </div>
            </>
          )}

          {err && (
            <p className="error-text">
              {isApiError(err) ? err.message : 'Could not send. Try again.'}
            </p>
          )}
          {done && <p className="bc__done">{done}</p>}

          <button
            className="btn"
            disabled={!canSend || busy}
            onClick={() => {
              setDone(null);
              if (tab === 'message') sendMessage.mutate();
              else sendEvent.mutate();
            }}
            style={{ marginTop: 12 }}
          >
            {busy
              ? 'Sending…'
              : `Send to ${ids.length} ${ids.length === 1 ? 'family' : 'families'}`}
          </button>
        </>
      )}
    </div>
  );
}
